import React from "react";
import { FlexRowAlignCenter } from "../Flex";
import LinkWrapper from "../wrappers/Link/Link";
import { BREADCRUMB_ROOTS } from "./utils";
import { ChevronRight } from "lucide-react";

export type BreadcrumbItem = {
    label: string;
    href?: string;
    // if there's is onClick, it will override the href
    onClick?: () => void;
};

export type BreadcrumbProps<T extends BreadcrumbItem> = {
    root: "home" | "explore" | "exams" | "blog";
    customRoot?: T;
    items: T[];
    /** component that separate each item */
    Separator?: React.ReactNode;
    /** component/wrapper that render each item. */
    renderSlug?: (props: { item: T; i: number }) => React.ReactNode;
    /** style of the breadcrumb outer container */
    style?: React.CSSProperties;
    /** classname of the breadcrumb outer container */
    className?: string;
};

const SlugDefault = ({ item, children }: { item: BreadcrumbItem; children: React.ReactNode }) => {
    if (item?.href) {
        return (
            <LinkWrapper href={item.href} className="secondaryTextBold1" onClick={item?.onClick}>
                {children}
            </LinkWrapper>
        );
    }

    if (item?.onClick) return <span onClick={item.onClick}>{children}</span>;

    return <span className="secondaryTextBold1">{children}</span>;
};

const Breadcrumb = <T extends BreadcrumbItem>({
    root,
    customRoot,
    items: items_,
    style,
    className,
    Separator = <ChevronRight size={24} />,
    renderSlug,
}: BreadcrumbProps<T>) => {
    const items = customRoot ? [customRoot, ...items_] : [BREADCRUMB_ROOTS[root], ...items_];

    return (
        <FlexRowAlignCenter className={className} style={style} as="nav">
            {items.map((item, i) => {
                const withSeparator = i !== items.length - 1;

                return (
                    <React.Fragment key={item.label + i}>
                        {typeof renderSlug === "function" ? (
                            renderSlug({ item: item as T, i })
                        ) : (
                            <SlugDefault key={item.label + i} item={item}>
                                {item.label}
                            </SlugDefault>
                        )}
                        {withSeparator && Separator}
                    </React.Fragment>
                );
            })}
        </FlexRowAlignCenter>
    );
};

export default Breadcrumb;
